import { type FC, type PropsWithChildren, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { UserInfoContext } from '@/contexts/UserInfoContext';

/**
 * Route guard component that requires the user to have an admin role.
 * If the user doesn't have an admin role, they are redirected to the home page.
 *
 * Enforcement triggers when EITHER `rbacEnabled` OR `rbvcEnabled` is on for
 * the tenant. Historically the studio assumed any logged-in tenant member
 * was admin (no enforcement = `enableRbac ? isAdmin : true`); RBVC makes
 * this assumption unsafe because the whole point of RBVC is to give
 * members a restricted view, and a member who can still navigate to
 * /admin/role-views could re-grant themselves access. Including RBVC in
 * the enforcement gate closes that hole without requiring the full RBAC
 * rollout. Uses server-sourced isAdmin from the session resolver
 * (DB roles + JWT realm-role check).
 *
 * ### Role-impersonation carve-out
 *
 * When an operator enters a child tenant via "Enter as <Role>", the
 * server-side `clear_operator_overlay/1` strips `:is_admin` and
 * `:operator` from ctx, so `userInfo.isAdmin` is `false` even when the
 * assumed role (e.g. `tenant_admin`) is admin-tier within the child.
 * The right authority during impersonation is the assumed role's
 * feature grants — `FeatureRouteGuard` reads those via
 * `userInfo.assumedRoleFeatures` and enforces the per-URL feature gate.
 *
 * So for an impersonation session we DEFER to `FeatureRouteGuard`: if
 * the assumed role doesn't grant the destination's feature, the guard
 * will redirect to `/`. Cedar PEP + GraphQL Boundary checks remain the
 * security boundary on every request — this carve-out just stops the
 * `isAdmin` overlay from black-holing the entire admin surface for any
 * operator who downgraded to a tenant-managed role.
 *
 * Usage:
 * ```tsx
 * <Route path="/admin/personas" element={
 *   <RequireAdmin>
 *     <AdminPersonasRoute />
 *   </RequireAdmin>
 * } />
 * ```
 */
export const RequireAdmin: FC<PropsWithChildren> = ({ children }) => {
  const { userInfo } = useContext(UserInfoContext);
  const navigate = useNavigate();
  const enableRbac = !!userInfo?.features?.rbacEnabled;
  const enableRbvc = !!userInfo?.features?.rbvcEnabled;
  const enforce = enableRbac || enableRbvc;
  const isAdmin = !!userInfo?.isAdmin;
  const impersonating = !!userInfo?.assumedRole;
  const denied = enforce && !isAdmin && !impersonating;

  useEffect(() => {
    if (denied) {
      navigate('/', { replace: true });
    }
  }, [denied, navigate]);

  if (denied) {
    return null;
  }

  return <>{children}</>;
};
